import React from "react";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Descriptions,
  Form,
  Image,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Typography,
  Upload,
} from "antd";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";

import { API_URL, getCredentials } from "../../shared/utils/Util";
import { centsToDollar, dollarToCents } from "../../shared/utils/TypedUtil";
import { useAntdTheme } from "../../shared/hooks";
import {
  ALL_DAYS_OF_WEEK_MASK,
  ItemSchemaOption,
  PrepaidCardSchema,
  PrepaidCardSchemaRequest,
  daysOfWeekIndicesToMask,
  daysOfWeekMaskToIndices,
} from "./types";

const { RangePicker } = DatePicker;
const { Text } = Typography;

type PrepaidCardFormValues = {
  name: string;
  description?: string;
  image_url?: string;
  price: number; // dollars, converted on submit
  tax_free: boolean;
  total_redeem_count: number;
  redeem_window: [Dayjs, Dayjs];
  redeem_days_of_week: number[];
  purchase_window: [Dayjs, Dayjs];
  item_schema_ids: string[];
};

type PrepaidCardFormProps = {
  restaurantId: string;
  schema?: PrepaidCardSchema; // undefined = create mode
  itemSchemaOptions: ItemSchemaOption[];
  loading: boolean;
  onSubmit: (req: PrepaidCardSchemaRequest) => void;
  onCancel: () => void;
};

function schemaToInitialValues(schema: PrepaidCardSchema): Partial<PrepaidCardFormValues> {
  return {
    name: schema.name,
    description: schema.description,
    image_url: schema.image_url,
    price: Number(centsToDollar(schema.price)),
    tax_free: schema.tax_free,
    total_redeem_count: schema.total_redeem_count,
    redeem_window: [dayjs(schema.redeem_start_time), dayjs(schema.redeem_end_time)],
    redeem_days_of_week: daysOfWeekMaskToIndices(schema.redeem_days_of_week),
    purchase_window: [dayjs(schema.purchase_start_time), dayjs(schema.purchase_end_time)],
    item_schema_ids: schema.item_schema_ids,
  };
}

export const PrepaidCardForm: React.FC<PrepaidCardFormProps> = ({
  restaurantId,
  schema,
  itemSchemaOptions,
  loading,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const [form] = Form.useForm<PrepaidCardFormValues>();
  const [imgUploading, setImgUploading] = React.useState(false);

  const isPublished = schema?.publish_time != null;

  React.useEffect(() => {
    if (schema) {
      form.setFieldsValue(schemaToInitialValues(schema));
    } else {
      form.setFieldsValue({
        tax_free: false,
        redeem_days_of_week: daysOfWeekMaskToIndices(ALL_DAYS_OF_WEEK_MASK),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema]);

  if (isPublished && schema) {
    // A published schema is immutable on the backend: render a read-only
    // summary instead of an editable form.
    const dayLabels = [
      t("day_sun"),
      t("day_mon"),
      t("day_tue"),
      t("day_wed"),
      t("day_thu"),
      t("day_fri"),
      t("day_sat"),
    ];
    const allowedDays = daysOfWeekMaskToIndices(schema.redeem_days_of_week)
      .map((d) => dayLabels[d])
      .join(", ");
    const itemNames = schema.item_schema_ids
      .map((id) => itemSchemaOptions.find((o) => o.id === id)?.name ?? id.slice(0, 8))
      .join(", ");
    return (
      <div>
        <Alert
          type="info"
          showIcon
          message={t("prepaid_card_published_readonly_notice")}
          style={{ marginBottom: token.marginMD }}
        />
        {schema.image_url && (
          <Image
            src={schema.image_url}
            alt={schema.name}
            width={160}
            style={{ marginBottom: token.marginMD, borderRadius: token.borderRadius }}
          />
        )}
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label={t("name")}>{schema.name}</Descriptions.Item>
          <Descriptions.Item label={t("description")}>
            {schema.description || "—"}
          </Descriptions.Item>
          <Descriptions.Item label={t("prepaid_card_price")}>
            ${centsToDollar(schema.price)}
          </Descriptions.Item>
          <Descriptions.Item label={t("prepaid_card_tax_free")}>
            {schema.tax_free ? t("yes") : t("no")}
          </Descriptions.Item>
          <Descriptions.Item label={t("prepaid_card_redeem_count")}>
            {schema.total_redeem_count}
          </Descriptions.Item>
          <Descriptions.Item label={t("prepaid_card_supported_items")}>
            {itemNames}
          </Descriptions.Item>
          <Descriptions.Item label={t("prepaid_card_redeem_window")}>
            {dayjs(schema.redeem_start_time).format("YYYY-MM-DD HH:mm")} –{" "}
            {dayjs(schema.redeem_end_time).format("YYYY-MM-DD HH:mm")} ({t("exclusive_end")})
          </Descriptions.Item>
          <Descriptions.Item label={t("prepaid_card_redeem_days")}>{allowedDays}</Descriptions.Item>
          <Descriptions.Item label={t("prepaid_card_purchase_window")}>
            {dayjs(schema.purchase_start_time).format("YYYY-MM-DD HH:mm")} –{" "}
            {dayjs(schema.purchase_end_time).format("YYYY-MM-DD HH:mm")} ({t("exclusive_end")})
          </Descriptions.Item>
        </Descriptions>
        <Form.Item style={{ marginTop: token.marginMD }}>
          <Button onClick={onCancel}>{t("close")}</Button>
        </Form.Item>
      </div>
    );
  }

  const handleFinish = (values: PrepaidCardFormValues) => {
    const req: PrepaidCardSchemaRequest = {
      name: values.name.trim(),
      description: values.description?.trim(),
      image_url: values.image_url,
      price: dollarToCents(values.price),
      tax_free: values.tax_free ?? false,
      total_redeem_count: Math.round(values.total_redeem_count),
      redeem_start_time: values.redeem_window[0].toISOString(),
      redeem_end_time: values.redeem_window[1].toISOString(),
      redeem_days_of_week: daysOfWeekIndicesToMask(values.redeem_days_of_week),
      purchase_start_time: values.purchase_window[0].toISOString(),
      purchase_end_time: values.purchase_window[1].toISOString(),
      item_schema_ids: values.item_schema_ids,
    };
    onSubmit(req);
  };

  const handleUploadChange = (info: {
    file: { status?: string; response?: { s3_url: string } };
  }) => {
    if (info.file.status === "uploading") {
      setImgUploading(true);
    } else if (info.file.status === "done") {
      setImgUploading(false);
      form.setFieldsValue({ image_url: info.file.response?.s3_url });
    } else if (info.file.status === "error") {
      setImgUploading(false);
    }
  };

  const imageUrl = Form.useWatch("image_url", form);

  const dayOptions = [
    { label: t("day_sun"), value: 0 },
    { label: t("day_mon"), value: 1 },
    { label: t("day_tue"), value: 2 },
    { label: t("day_wed"), value: 3 },
    { label: t("day_thu"), value: 4 },
    { label: t("day_fri"), value: 5 },
    { label: t("day_sat"), value: 6 },
  ];

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish}>
      <Form.Item name="image_url" hidden />
      <Form.Item label={t("prepaid_card_image")}>
        <Upload
          name="img"
          headers={{ Authorization: `Basic ${getCredentials()}` }}
          accept=".jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp"
          showUploadList={false}
          listType="picture-card"
          action={`${API_URL}/v1/r/${restaurantId}/user_content/item_images`}
          onChange={handleUploadChange}
        >
          {imgUploading ? (
            <LoadingOutlined />
          ) : imageUrl ? (
            <img src={imageUrl} alt={t("prepaid_card_image")} style={{ width: "100%" }} />
          ) : (
            <div>
              <PlusOutlined />
              <div style={{ marginTop: 8 }}>{t("upload_image")}</div>
            </div>
          )}
        </Upload>
      </Form.Item>

      <Form.Item
        name="name"
        label={t("name")}
        rules={[{ required: true, message: t("name_is_required") }]}
      >
        <Input placeholder={t("prepaid_card_name_placeholder")} />
      </Form.Item>

      <Form.Item name="description" label={t("description")}>
        <Input.TextArea rows={2} placeholder={t("description_placeholder")} />
      </Form.Item>

      <Space style={{ display: "flex" }} align="start">
        <Form.Item
          name="price"
          label={t("prepaid_card_price")}
          rules={[
            { required: true, message: t("prepaid_card_price_is_required") },
            {
              validator: (_, value: number) =>
                value > 0
                  ? Promise.resolve()
                  : Promise.reject(new Error(t("prepaid_card_price_positive"))),
            },
          ]}
          style={{ width: 200 }}
        >
          <InputNumber min={0} precision={2} prefix="$" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="total_redeem_count"
          label={t("prepaid_card_redeem_count")}
          rules={[
            { required: true, message: t("prepaid_card_redeem_count_is_required") },
            {
              validator: (_, value: number) =>
                value > 0
                  ? Promise.resolve()
                  : Promise.reject(new Error(t("prepaid_card_redeem_count_positive"))),
            },
          ]}
          style={{ width: 200 }}
        >
          <InputNumber min={1} precision={0} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="tax_free" label={t("prepaid_card_tax_free")} valuePropName="checked">
          <Switch checkedChildren={t("yes")} unCheckedChildren={t("no")} />
        </Form.Item>
      </Space>

      <Form.Item
        name="item_schema_ids"
        label={t("prepaid_card_supported_items")}
        rules={[
          {
            required: true,
            type: "array",
            min: 1,
            message: t("prepaid_card_supported_items_required"),
          },
        ]}
      >
        <Select
          mode="multiple"
          showSearch
          optionFilterProp="label"
          placeholder={t("prepaid_card_select_items")}
          options={itemSchemaOptions.map((i) => ({ value: i.id, label: i.name }))}
        />
      </Form.Item>

      <Form.Item
        name="redeem_window"
        label={t("prepaid_card_redeem_window")}
        rules={[{ required: true, message: t("prepaid_card_redeem_window_required") }]}
      >
        <RangePicker
          showTime
          style={{ width: "100%" }}
          placeholder={[t("start_date"), `${t("end_date")} (${t("exclusive_end")})`]}
        />
      </Form.Item>
      <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
        {t("prepaid_card_redeem_window_help")}
      </Text>

      <Form.Item
        name="redeem_days_of_week"
        label={t("prepaid_card_redeem_days")}
        rules={[
          {
            required: true,
            type: "array",
            min: 1,
            message: t("prepaid_card_redeem_days_required"),
          },
        ]}
        style={{ marginTop: token.marginSM }}
      >
        <Checkbox.Group options={dayOptions} />
      </Form.Item>

      <Form.Item
        name="purchase_window"
        label={t("prepaid_card_purchase_window")}
        rules={[{ required: true, message: t("prepaid_card_purchase_window_required") }]}
      >
        <RangePicker
          showTime
          style={{ width: "100%" }}
          placeholder={[t("start_date"), `${t("end_date")} (${t("exclusive_end")})`]}
        />
      </Form.Item>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            {schema ? t("save") : t("create")}
          </Button>
          <Button onClick={onCancel}>{t("cancel")}</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};
